"use client"

import type { ChangeEvent, InputHTMLAttributes, ReactNode, Ref } from "react"

import { useFieldA11y, type FieldSize, type FieldState } from "@/components/ui/field"

interface DateInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "value" | "defaultValue" | "onChange" | "type" | "inputMode" | "placeholder"> {
  autoComplete: string
  template: string
  value: string
  onChange: (value: string) => void
  label?: string
  help?: ReactNode
  state?: FieldState
  size?: FieldSize
  ref?: Ref<HTMLInputElement>
}

const ISO_DATE = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/
const SEPARATOR = /[/.\- ]/

const closeShortParts = (raw: string): string => {
  const parts = raw.split(SEPARATOR)
  return parts.map((part, index) => (index < 2 && index < parts.length - 1 && /^\d$/.test(part) ? `0${part}` : part)).join("/")
}

const isDeletion = (event: ChangeEvent<HTMLInputElement>, previous: string): boolean => {
  const { inputType } = event.nativeEvent as InputEvent
  return inputType ? inputType.startsWith("delete") : event.target.value.length <= previous.length
}

const maskDate = (raw: string, deleting: boolean): string => {
  const iso = ISO_DATE.exec(raw)
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
  const digits = (deleting ? raw : closeShortParts(raw)).replace(/\D/g, "").slice(0, 8)
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join("/")
}

export const DateInput = ({ label, help, state, size, template, value, onChange, disabled, className, id, ref, ...rest }: DateInputProps) => {
  const { fieldId, helpId, styles, controlProps } = useFieldA11y({ id, help, state, size, disabled })
  const remaining = value ? template.slice(value.length) : ""
  return (
    <div className={styles.root({ className })}>
      {label && (
        <label htmlFor={fieldId} className={styles.label()}>
          {label}
        </label>
      )}
      <div className={styles.control({ className: "flex items-center" })}>
        <span className="relative flex flex-1 self-stretch">
          {remaining && (
            <span data-testid="date-input-template" aria-hidden className={styles.input({ className: "pointer-events-none absolute inset-0 flex items-center whitespace-pre" })}>
              <span className="invisible">{value}</span>
              <span className={size === "lg" ? "text-fg-placeholder" : "text-fg-muted"}>{remaining}</span>
            </span>
          )}
          <input
            ref={ref}
            disabled={disabled}
            {...rest}
            {...controlProps}
            type="text"
            inputMode="numeric"
            placeholder={template}
            value={value}
            onChange={(event) => onChange(maskDate(event.target.value, isDeletion(event, value)))}
            className={styles.input({ className: `relative ${disabled ? "text-fg-muted cursor-not-allowed" : ""}` })}
          />
        </span>
      </div>
      {help && (
        <div id={helpId} className={styles.help()}>
          {help}
        </div>
      )}
    </div>
  )
}
